import "@/assets/styles/global.css";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MotionZone } from "@/motion";
import { Provider } from "react-redux";
import { store } from "@/app/store";
import { AuthProvider } from "@/app/hooks/useAuth";
import { AccountAuthProvider } from "@/app/hooks/useAccountAuth";
import { SavedJobsProvider } from "@/app/hooks/useSavedJobs";
import { useTokenRefresh } from "@/app/hooks/useTokenRefresh";
import ScrollToTop from "@/components/common/ScrollToTop";
import { SmoothScroll } from "@/motion/SmoothScroll";
import { SiteChromeProvider } from "@/app/SiteChrome";
import { FEATURES } from "@/config/features";
import { ErrorBoundary } from "./observability/ErrorBoundary";
import { EnvironmentPausedGate } from "@/components/common/EnvironmentPausedGate";

import LandingDcV2 from "./app/routes/LandingDcV2";
import NotFound from "./app/routes/NotFound";
import Solutions from "./app/routes/Solutions";
import Pricing from "./app/routes/Pricing";
import Contact from "./app/routes/Contact";
import About from "./app/routes/About";
import MeetOurCeo from "./app/routes/MeetOurCeo";
import Security from "./app/routes/Security";
import Privacy from "./app/routes/Privacy";
import Terms from "./app/routes/Terms";
import DataDeletion from "./app/routes/DataDeletion";
import JobPostings from "./app/routes/JobPostings";
import JobDetails from "./app/routes/JobDetails";
import JobApplication from "./app/routes/JobApplication";
import ApplicationSubmitted from "./app/routes/ApplicationSubmitted";
import Profile from "./app/routes/Profile";
import ProfileApplicationChecklist from "./app/routes/ProfileApplicationChecklist";
import Auth from "./app/routes/Auth";
import ResetPassword from "./app/routes/ResetPassword";
import VerifyEmail from "./app/routes/VerifyEmail";
import AccountSignIn from "./app/routes/account/AccountSignIn";
import AccountOverview from "./app/routes/account/AccountOverview";
import AccountSubscription from "./app/routes/account/AccountSubscription";
import AccountBilling from "./app/routes/account/AccountBilling";
import AccountOrganization from "./app/routes/account/AccountOrganization";
import AccountProfile from "./app/routes/account/AccountProfile";
import AccountSecurity from "./app/routes/account/AccountSecurity";
import AccountShell from "@/components/account/AccountShell";
import RequireAccount, { OwnerOnly } from "@/components/account/RequireAccount";
import Forum from "./app/routes/Forum";
import ForumSubforum from "./app/routes/ForumSubforum";
import ForumPost from "./app/routes/ForumPost";
import ForumArchive from "./app/routes/ForumArchive";

const queryClient = new QueryClient();

function AppContent() {
  useTokenRefresh();

  return (
    <AuthProvider>
      <AccountAuthProvider>
      <SavedJobsProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Mounted above the routes so one instance spans every navigation
            instead of being torn down and rebuilt per page. */}
        <SmoothScroll />
        <ScrollToTop />
        <MotionZone>
        <SiteChromeProvider>
        <Routes>
          {/* The homepage is the 1:1 port of the AI2Aim Landing v2 design
              canvas. It ships its own nav and footer, so it deliberately
              bypasses SiteLayout — which also means its header links are the
              design's in-page anchors rather than routes.

              The previous v4/v3 homepage still exists at
              app/routes/Index.tsx; it is simply no longer routed. */}
          <Route path="/" element={<LandingDcV2 />} />
          <Route path="/solutions" element={<Solutions />} />
          {FEATURES.pricing && <Route path="/pricing" element={<Pricing />} />}
          <Route path="/about" element={<About />} />
          <Route path="/about/ceo" element={<MeetOurCeo />} />
          <Route path="/security" element={<Security />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          {FEATURES.jobs && (
            <>
              <Route path="/jobs" element={<JobPostings />} />
              <Route path="/job/:jobId" element={<JobDetails />} />
              <Route path="/apply/:jobId" element={<JobApplication />} />
              <Route path="/application-submitted/:jobId" element={<ApplicationSubmitted />} />
              <Route path="/profile" element={<Profile />} />
              {/* Link in the activation checklist email (T102126038):
                  {FRONTEND_JMS_URL}/profile/applications/{job_postings.id}. */}
              <Route path="/profile/applications/:jobPostingId" element={<ProfileApplicationChecklist />} />
            </>
          )}
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Organization account console — internal realm, separate session
              from the candidate portal above. */}
          <Route path="/account/sign-in" element={<AccountSignIn />} />
          <Route
            path="/account"
            element={
              <RequireAccount>
                <AccountShell />
              </RequireAccount>
            }
          >
            <Route index element={<AccountOverview />} />
            <Route
              path="subscription"
              element={
                <OwnerOnly>
                  <AccountSubscription />
                </OwnerOnly>
              }
            />
            <Route
              path="billing"
              element={
                <OwnerOnly>
                  <AccountBilling />
                </OwnerOnly>
              }
            />
            <Route path="organization" element={<AccountOrganization />} />
            <Route path="profile" element={<AccountProfile />} />
            <Route path="security" element={<AccountSecurity />} />
          </Route>

          {FEATURES.forum && (
            <>
              <Route path="/forum" element={<Forum />} />
              <Route path="/forum/:slug" element={<ForumSubforum />} />
              <Route path="/forum/post/:postId" element={<ForumPost />} />
              <Route path="/forum/archive" element={<ForumArchive />} />
            </>
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </SiteChromeProvider>
        </MotionZone>
      </BrowserRouter>
      </SavedJobsProvider>
      </AccountAuthProvider>
    </AuthProvider>
  );
}

const App = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ErrorBoundary>
            <EnvironmentPausedGate>
              <AppContent />
            </EnvironmentPausedGate>
          </ErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;

